/**
 * Frontend Performance Monitoring Service
 * Implements Core Web Vitals tracking, custom metrics, and HIPAA-compliant monitoring
 * for Project Lighthouse healthcare application
 */

import { getCLS, getFID, getLCP, getFCP, getTTFB, getINP } from 'web-vitals';

// Performance budget thresholds
const PERFORMANCE_BUDGETS = {
  LCP: 2500,  // 2.5 seconds
  FID: 100,   // 100 milliseconds
  CLS: 0.1,   // Layout shift score
  INP: 200,   // 200 milliseconds (new metric)
  FCP: 1800,  // 1.8 seconds
  TTFB: 800,  // 800 milliseconds
  // Custom healthcare metrics
  TTI_PatientDataForm: 1000,     // 1 second
  TTI_SupervisionForm: 1200,     // 1.2 seconds
  TTI_Dashboard: 2000,           // 2 seconds
  API_PatientLoad: 1500,         // 1.5 seconds
  API_SupervisionSubmit: 2000,   // 2 seconds
  PDF_Generation: 5000,          // 5 seconds
} as const;

// Types for performance metrics
interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  label: string;
  timestamp: number;
  url: string;
  deviceInfo?: DeviceInfo;
  metadata?: Record<string, any>;
}

interface DeviceInfo {
  deviceMemory?: number;
  hardwareConcurrency?: number;
  connectionType?: string;
  viewportSize: { width: number; height: number };
  userAgent: string;
}

interface CustomMetric {
  name: string;
  value: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitoringService {
  private static instance: PerformanceMonitoringService;
  private initialized = false;
  private deviceInfo: DeviceInfo;
  private metrics: PerformanceMetric[] = [];
  private observers: Map<string, PerformanceObserver> = new Map();

  private constructor() {
    this.deviceInfo = this.collectDeviceInfo();
  }

  static getInstance(): PerformanceMonitoringService {
    if (!PerformanceMonitoringService.instance) {
      PerformanceMonitoringService.instance = new PerformanceMonitoringService();
    }
    return PerformanceMonitoringService.instance;
  }

  /**
   * Initialize performance monitoring with Core Web Vitals
   */
  initialize(): void {
    if (this.initialized) return;

    // Initialize Core Web Vitals monitoring
    this.initializeCoreWebVitals();
    
    // Initialize custom performance observers
    this.initializeCustomObservers();
    
    // Initialize Real User Monitoring
    this.initializeRUM();
    
    this.initialized = true;
    console.log('🚀 Performance monitoring initialized');
  }

  /**
   * Track Core Web Vitals using the web-vitals library
   */
  private initializeCoreWebVitals(): void {
    const handleMetric = (metric: any) => {
      this.recordMetric({
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        label: 'Core Web Vitals',
        timestamp: Date.now(),
        url: window.location.href,
        deviceInfo: this.deviceInfo,
        metadata: {
          id: metric.id,
          delta: metric.delta,
          entries: metric.entries?.length || 0
        }
      });
    };

    // Register all Core Web Vitals
    getCLS(handleMetric);
    getFID(handleMetric);
    getLCP(handleMetric);
    getFCP(handleMetric);
    getTTFB(handleMetric);
    getINP(handleMetric); // New INP metric for 2024
  }

  /**
   * Initialize custom performance observers for healthcare-specific metrics
   */
  private initializeCustomObservers(): void {
    // Long Task Observer (for FID/INP optimization)
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.duration > 50) { // Tasks longer than 50ms
              this.recordMetric({
                name: 'LongTask',
                value: entry.duration,
                rating: entry.duration > 100 ? 'poor' : 'needs-improvement',
                label: 'Long Task Detection',
                timestamp: Date.now(),
                url: window.location.href,
                metadata: {
                  startTime: entry.startTime,
                  entryType: entry.entryType
                }
              });
            }
          });
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
        this.observers.set('longtask', longTaskObserver);
      } catch (error) {
        console.warn('Long task observer not supported:', error);
      }

      // Navigation Observer
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry: any) => {
            this.recordMetric({
              name: 'NavigationTiming',
              value: entry.loadEventEnd - entry.fetchStart,
              rating: this.getRating(entry.loadEventEnd - entry.fetchStart, 'NavigationTiming'),
              label: 'Navigation Performance',
              timestamp: Date.now(),
              url: window.location.href,
              metadata: {
                domContentLoaded: entry.domContentLoadedEventEnd - entry.fetchStart,
                domInteractive: entry.domInteractive - entry.fetchStart,
                loadComplete: entry.loadEventEnd - entry.fetchStart
              }
            });
          });
        });
        navigationObserver.observe({ entryTypes: ['navigation'] });
        this.observers.set('navigation', navigationObserver);
      } catch (error) {
        console.warn('Navigation observer not supported:', error);
      }
    }
  }

  /**
   * Initialize Real User Monitoring
   */
  private initializeRUM(): void {
    // Track initial page load performance
    this.recordMetric({
      name: 'RUM_Initialize',
      value: performance.now(),
      rating: 'good',
      label: 'Real User Monitoring',
      timestamp: Date.now(),
      url: window.location.href,
      deviceInfo: this.deviceInfo,
      metadata: {
        initialLoad: true,
        sessionId: this.generateSessionId()
      }
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.recordMetric({
        name: 'PageVisibilityChange',
        value: performance.now(),
        rating: 'good',
        label: 'User Engagement',
        timestamp: Date.now(),
        url: window.location.href,
        metadata: {
          visibility: document.visibilityState
        }
      });
    });
  }

  /**
   * Track custom healthcare application metrics
   */
  trackCustomMetric(metric: CustomMetric): void {
    this.recordMetric({
      name: metric.name,
      value: metric.value,
      rating: this.getRating(metric.value, metric.name),
      label: 'Custom Metric',
      timestamp: Date.now(),
      url: window.location.href,
      metadata: metric.metadata
    });
  }

  /**
   * Track time to interactive for specific components
   */
  measureTimeToInteractive(componentName: string): () => void {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const timeToInteractive = endTime - startTime;
      
      this.trackCustomMetric({
        name: `TTI_${componentName}`,
        value: timeToInteractive,
        metadata: {
          component: componentName,
          startTime,
          endTime
        }
      });
    };
  }

  /**
   * Track API call performance
   */
  trackAPICall(endpoint: string, duration: number, success: boolean): void {
    this.trackCustomMetric({
      name: `API_${endpoint.replace(/\//g, '_')}`,
      value: duration,
      metadata: {
        endpoint,
        success,
        method: 'performance_tracking'
      }
    });
  }

  /**
   * Track user interactions (HIPAA-compliant)
   */
  trackUserInteraction(interactionName: string, metadata: Record<string, any> = {}): void {
    // Sanitize metadata to ensure no PHI is included
    const sanitizedMetadata = this.sanitizeMetadata(metadata);
    
    this.trackCustomMetric({
      name: `Interaction_${interactionName}`,
      value: performance.now(),
      metadata: sanitizedMetadata
    });
  }

  /**
   * Track form submission performance
   */
  trackFormSubmission(formType: string, duration: number, success: boolean): void {
    this.trackCustomMetric({
      name: `FormSubmit_${formType}`,
      value: duration,
      metadata: {
        formType,
        success,
        timestamp: Date.now()
      }
    });
  }

  /**
   * Track PDF generation performance
   */
  trackPDFGeneration(pdfType: string, duration: number, success: boolean): void {
    this.trackCustomMetric({
      name: 'PDF_Generation',
      value: duration,
      metadata: {
        pdfType,
        success,
        timestamp: Date.now()
      }
    });
  }

  /**
   * Get current performance summary
   */
  getPerformanceSummary(): {
    metrics: PerformanceMetric[];
    budgetViolations: string[];
    summary: Record<string, { avg: number; count: number; rating: string }>;
  } {
    const budgetViolations: string[] = [];
    const summary: Record<string, { avg: number; count: number; rating: string }> = {};

    // Group metrics by name
    const groupedMetrics = this.metrics.reduce((acc, metric) => {
      if (!acc[metric.name]) acc[metric.name] = [];
      acc[metric.name].push(metric);
      return acc;
    }, {} as Record<string, PerformanceMetric[]>);

    // Calculate summary statistics
    Object.entries(groupedMetrics).forEach(([name, metrics]) => {
      const values = metrics.map(m => m.value);
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      const latestRating = metrics[metrics.length - 1].rating;
      
      summary[name] = {
        avg: Math.round(avg * 100) / 100,
        count: metrics.length,
        rating: latestRating
      };

      // Check budget violations
      const budget = PERFORMANCE_BUDGETS[name as keyof typeof PERFORMANCE_BUDGETS];
      if (budget && avg > budget) {
        budgetViolations.push(`${name}: ${avg.toFixed(2)}ms (budget: ${budget}ms)`);
      }
    });

    return {
      metrics: this.metrics.slice(-50), // Last 50 metrics
      budgetViolations,
      summary
    };
  }

  /**
   * Export performance data for analysis
   */
  exportPerformanceData(): string {
    const summary = this.getPerformanceSummary();
    const exportData = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      deviceInfo: this.deviceInfo,
      ...summary
    };
    
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Private helper methods
   */
  private recordMetric(metric: PerformanceMetric): void {
    // Check performance budget
    this.checkPerformanceBudget(metric);
    
    // Store metric (keep only last 100 metrics in memory)
    this.metrics.push(metric);
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    // Send to analytics (in production, this would go to your monitoring service)
    this.sendToAnalytics(metric);
  }

  private checkPerformanceBudget(metric: PerformanceMetric): void {
    const budget = PERFORMANCE_BUDGETS[metric.name as keyof typeof PERFORMANCE_BUDGETS];
    if (budget && metric.value > budget) {
      console.warn(`⚠️ Performance budget exceeded for ${metric.name}:`, {
        value: metric.value,
        budget,
        url: metric.url
      });
      
      // In production, you might want to send alerts to your monitoring system
      this.alertPerformanceIssue(metric, budget);
    }
  }

  private alertPerformanceIssue(metric: PerformanceMetric, budget: number): void {
    // Implement your alerting logic here
    // This could send to your monitoring service, log to console, etc.
    console.warn(`Performance budget exceeded for ${metric.name}: ${metric.value} > ${budget}`);
  }

  private getRating(value: number, metricName: string): 'good' | 'needs-improvement' | 'poor' {
    const budget = PERFORMANCE_BUDGETS[metricName as keyof typeof PERFORMANCE_BUDGETS];
    if (!budget) return 'good';
    
    if (value <= budget * 0.75) return 'good';
    if (value <= budget) return 'needs-improvement';
    return 'poor';
  }

  private collectDeviceInfo(): DeviceInfo {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    return {
      deviceMemory: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      connectionType: connection ? connection.effectiveType : 'unknown',
      viewportSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      userAgent: navigator.userAgent
    };
  }

  private sanitizeMetadata(metadata: Record<string, any>): Record<string, any> {
    // Remove any potential PHI from metadata
    const sanitized = { ...metadata };
    
    // Remove fields that might contain PHI
    const phiFields = ['patientId', 'employeeId', 'ssn', 'dob', 'phone', 'email', 'name', 'address'];
    phiFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Replace any pattern that looks like patient/employee IDs
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'string') {
        sanitized[key] = sanitized[key].replace(/[A-Z]{3}\d{6}/g, '[REDACTED]');
      }
    });

    return sanitized;
  }

  private sendToAnalytics(metric: PerformanceMetric): void {
    // Ensure no PHI is included in the metric
    const sanitizedMetric = {
      ...metric,
      metadata: metric.metadata ? this.sanitizeMetadata(metric.metadata) : undefined
    };

    // In development, log to console
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Performance Metric:', sanitizedMetric);
      return;
    }

    // In production, send to your HIPAA-compliant analytics service
    // Example implementation:
    /*
    fetch('/api/analytics/performance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(sanitizedMetric)
    }).catch(error => {
      console.error('Failed to send performance metric:', error);
    });
    */
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Cleanup method to disconnect observers
   */
  disconnect(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitoringService.getInstance();

// Export types for use in components
export type { PerformanceMetric, CustomMetric, DeviceInfo };